"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, Language } from '@/lib/translations';
import { invalidateOnLanguageChange } from '@/lib/cache/CacheInvalidation';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, variables?: Record<string, string>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children, initialLanguage }: {
  children: React.ReactNode;
  /**
   * Fixed starting language, rendered on the server too (no flash), and the
   * saved preference is not read over it. The embed widget passes the host
   * page's language here.
   */
  initialLanguage?: Language;
}) {
  // Always initialize with default to prevent hydration mismatch
  const [language, setLanguageState] = useState<Language>(initialLanguage ?? 'uk');

  // Sync with localStorage after mount to prevent hydration mismatch
  useEffect(() => {
    if (initialLanguage) return;
    try {
      const savedLanguage = localStorage.getItem('language') as Language;
      if (savedLanguage && (savedLanguage === 'uk' || savedLanguage === 'en')) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Necessary for hydration-safe localStorage sync
        setLanguageState(savedLanguage);
      } else {
        localStorage.setItem('language', 'uk');
      }
    } catch {
      // localStorage unavailable (e.g., cross-origin iframe in Safari/Firefox)
    }
  }, [initialLanguage]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem('language', lang);
    } catch {
      // localStorage unavailable in iframe context
    }

    // Invalidate any language-specific caches
    invalidateOnLanguageChange(lang);
  };

  // Cross-tab synchronization - listen for language changes in other tabs
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent<{ language: string | null }>) => {
      const newLanguage = event.detail.language;
      if (newLanguage && (newLanguage === 'uk' || newLanguage === 'en')) {
        setLanguageState(newLanguage as Language);
      }
    };

    window.addEventListener('language-changed', handleLanguageChange as EventListener);

    return () => {
      window.removeEventListener('language-changed', handleLanguageChange as EventListener);
    };
  }, []);

  const t = (key: string, variables?: Record<string, string>): string => {
    const keys = key.split('.');
    let value: unknown = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = (value as Record<string, unknown>)[k];
      } else {
        // Fallback to English if key not found
        value = translations.en;
        for (const k2 of keys) {
          if (value && typeof value === 'object' && k2 in value) {
            value = (value as Record<string, unknown>)[k2];
          } else {
            return key; // Return the key itself if not found
          }
        }
        break;
      }
    }

    let result = typeof value === 'string' ? value : key;

    // Replace template variables like {{variableName}}
    if (variables) {
      Object.keys(variables).forEach((varKey) => {
        result = result.replace(new RegExp(`{{${varKey}}}`, 'g'), variables[varKey]);
      });
    }

    return result;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

/**
 * Safe translation function for use outside of LanguageProvider context.
 * Use this in error boundaries and other edge cases where context may not be available.
 * Reads language preference directly from localStorage.
 */
export function getSafeTranslation(key: string): string {
  // Determine language from localStorage (client-side only)
  let language: Language = 'uk';
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('language');
      if (saved === 'uk' || saved === 'en') {
        language = saved;
      }
    } catch {
      // localStorage unavailable in iframe context
    }
  }

  // Navigate to the translation value
  const keys = key.split('.');
  let value: unknown = translations[language];

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      return key; // Return key if not found
    }
  }

  return typeof value === 'string' ? value : key;
}